/**
 * Blog URL Utilities
 * Centralized helper for generating blog URLs with subdomain support
 */

import { SEO_CONFIG } from '@/lib/seo-config';

/**
 * Get blog base URL (subdomain)
 */
export function getBlogBaseUrl(): string {
  return SEO_CONFIG.blogDomain;
}

/**
 * Get blog post URL
 */
export function getBlogPostUrl(slug: string): string {
  return `${SEO_CONFIG.blogDomain}/${slug}`;
}

/**
 * Get blog tag URL
 */
export function getBlogTagUrl(tag: string): string {
  return `${SEO_CONFIG.blogDomain}/tag/${encodeURIComponent(tag)}`;
}

/**
 * Get blog category URL
 */
export function getBlogCategoryUrl(category: string): string {
  return `${SEO_CONFIG.blogDomain}/category/${encodeURIComponent(category)}`;
}

/**
 * Check if we're on the blog subdomain
 */
export function isBlogSubdomain(): boolean {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname.startsWith('blogs.') || hostname.includes('.blogs.');
}

/**
 * Get internal blog link (for same-subdomain navigation)
 * Returns relative path if on blog subdomain, absolute URL otherwise
 */
export function getInternalBlogLink(path: string): string {
  if (isBlogSubdomain()) {
    // We're on blog subdomain, use relative links
    return path.startsWith('/') ? path : `/${path}`;
  }
  // We're on main domain, use absolute blog subdomain URL
  return `${SEO_CONFIG.blogDomain}${path.startsWith('/') ? path : `/${path}`}`;
}
